import { useGetRentalApplications } from '@/hooks/rentalApplication';
import { RentalApplicationFilters } from '@/api/types/rentalApplication';
import {
  Box,
  Text,
  VStack,
  Table,
  HStack,
  Input,
  Select,
  createListCollection,
  Heading,
  Badge,
  Stat,
  Grid,
  GridItem,
  Skeleton,
} from '@chakra-ui/react';
import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Pagination from '@/components/Pagination';
import { useDebounce } from '@/hooks/useDebounce';
import { useDelayedLoading } from '@/hooks/useDelayedLoading';
import { CITY_OPTIONS } from '@/constants/cities';
import { IncomeType } from '@/types/rentalApplication';
import { InfoCard } from '@/components/InfoCard';

const getIncomeTypeLabel = (incomeType: IncomeType): string => {
  const labels: Record<IncomeType, string> = {
    [IncomeType.WORK]: 'Anställd',
    [IncomeType.SELF_EMPLOYED]: 'Egen företagare',
    [IncomeType.UNEMPLOYED]: 'A-kassa',
    [IncomeType.STUDIES]: 'Studier',
    [IncomeType.PENSION]: 'Pension',
    [IncomeType.OWN_COMPANY]: 'Eget företag',
    [IncomeType.ALLOWANCE]: 'Bidrag',
    [IncomeType.OTHER]: 'Övrigt',
  };
  return labels[incomeType] || incomeType;
};

const AdminView = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<RentalApplicationFilters>({
    page: 1,
    limit: 10,
  });

  // City collection for Select component
  const cityCollection = useMemo(() => {
    return createListCollection({
      items: [{ label: 'Alla städer', value: 'all' }, ...CITY_OPTIONS],
    });
  }, []);

  // Only debounce the search/filter fields, not pagination
  const searchFilters = useMemo(
    () => ({
      city: filters.city,
      date_from: filters.date_from,
      date_to: filters.date_to,
      min_rent: filters.min_rent,
      max_rent: filters.max_rent,
      smoker: filters.smoker,
    }),
    [
      filters.city,
      filters.date_from,
      filters.date_to,
      filters.min_rent,
      filters.max_rent,
      filters.smoker,
    ]
  );

  const debouncedSearchFilters = useDebounce(searchFilters, 200);

  // Combine debounced search filters with immediate pagination
  const finalFilters = useMemo(
    () => ({
      ...debouncedSearchFilters,
      page: filters.page,
      limit: filters.limit,
    }),
    [debouncedSearchFilters, filters.page, filters.limit]
  );

  const { data: response, isLoading, error } = useGetRentalApplications(finalFilters);
  const applications = response?.applications;
  const pagination = response?.pagination;

  // Use delayed loading to prevent flicker for fast API responses
  const delayedLoading = useDelayedLoading(isLoading);

  const updateFilter = (
    key: keyof RentalApplicationFilters,
    value: string | number | boolean | undefined
  ) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1, // Reset to first page when filters change
    }));
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({
      ...prev,
      page,
    }));
  };

  const handleItemsPerPageChange = (limit: number) => {
    setFilters(prev => ({
      ...prev,
      limit,
      page: 1, // Reset to first page when changing items per page
    }));
  };

  if (error) {
    return (
      <Box p={8} maxW="1400px" mx="auto">
        <InfoCard colorPalette="red">
          <Text color="red.fg" fontWeight="medium">
            Failed to load rental applications. Please try again later.
          </Text>
        </InfoCard>
      </Box>
    );
  }

  return (
    <Box p={{ base: 4, md: 8 }} maxW="1400px" mx="auto">
      <VStack gap={6} align="stretch">
        {/* Header Section */}
        <Box>
          <Heading size="2xl" mb={2}>
            Adminpanel
          </Heading>
          <Text color="fg.muted" fontSize="lg">
            Hantera hyresintresseanmälningar
          </Text>
        </Box>

        {/* Stats Section */}
        <Grid templateColumns={{ base: '1fr', md: 'repeat(4, 1fr)' }} gap={4}>
          <GridItem>
            <InfoCard>
              <Stat.Root>
                <Stat.Label>Totalt antal ansökningar</Stat.Label>
                <Stat.ValueText fontSize="3xl" fontWeight="bold">
                  {pagination?.total_count || 0}
                </Stat.ValueText>
              </Stat.Root>
            </InfoCard>
          </GridItem>
          <GridItem>
            <InfoCard>
              <Stat.Root>
                <Stat.Label>Visas på sidan</Stat.Label>
                <Stat.ValueText fontSize="3xl" fontWeight="bold">
                  {applications?.length || 0}
                </Stat.ValueText>
              </Stat.Root>
            </InfoCard>
          </GridItem>

          <GridItem>
            <InfoCard>
              <Stat.Root>
                <Stat.Label>Filter aktiva</Stat.Label>
                <Stat.ValueText fontSize="3xl" fontWeight="bold">
                  {Object.entries(filters).filter(
                    ([key, val]) => key !== 'page' && key !== 'limit' && val !== undefined
                  ).length || 0}
                </Stat.ValueText>
              </Stat.Root>
            </InfoCard>
          </GridItem>
        </Grid>

        {/* Filters Section */}
        <InfoCard
          title="Filtrera ansökningar"
          description="Använd filtren nedan för att hitta specifika ansökningar"
        >
          <VStack gap={4} align="stretch">
            <HStack gap={4} flexWrap="wrap">
              <Box flex={{ base: '1 1 100%', md: '1 1 200px' }}>
                <Text fontSize="sm" fontWeight="medium" mb={2}>
                  Stad
                </Text>
                <Select.Root
                  collection={cityCollection}
                  value={filters.city ? [filters.city] : ['all']}
                  onValueChange={details => {
                    const selectedValue = details.value[0];
                    updateFilter('city', selectedValue === 'all' ? undefined : selectedValue);
                  }}
                  positioning={{ strategy: 'fixed' }}
                  size="md"
                >
                  <Select.Control>
                    <Select.Trigger>
                      <Select.ValueText placeholder="Välj stad" />
                    </Select.Trigger>
                  </Select.Control>
                  <Select.Positioner>
                    <Select.Content>
                      {cityCollection.items.map(item => (
                        <Select.Item key={item.value} item={item}>
                          {item.label}
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Positioner>
                </Select.Root>
              </Box>

              <Box flex={{ base: '1 1 100%', md: '1 1 150px' }}>
                <Text fontSize="sm" fontWeight="medium" mb={2}>
                  Från datum
                </Text>
                <Input
                  type="date"
                  value={filters.date_from || ''}
                  onChange={e => updateFilter('date_from', e.target.value)}
                  size="md"
                />
              </Box>

              <Box flex={{ base: '1 1 100%', md: '1 1 150px' }}>
                <Text fontSize="sm" fontWeight="medium" mb={2}>
                  Till datum
                </Text>
                <Input
                  type="date"
                  value={filters.date_to || ''}
                  onChange={e => updateFilter('date_to', e.target.value)}
                  size="md"
                />
              </Box>

              <Box flex={{ base: '1 1 100%', md: '1 1 130px' }}>
                <Text fontSize="sm" fontWeight="medium" mb={2}>
                  Min hyra
                </Text>
                <Input
                  type="number"
                  placeholder="t.ex. 5000"
                  value={filters.min_rent || ''}
                  onChange={e => updateFilter('min_rent', parseInt(e.target.value) || undefined)}
                  size="md"
                />
              </Box>

              <Box flex={{ base: '1 1 100%', md: '1 1 130px' }}>
                <Text fontSize="sm" fontWeight="medium" mb={2}>
                  Max hyra
                </Text>
                <Input
                  type="number"
                  placeholder="t.ex. 15000"
                  value={filters.max_rent || ''}
                  onChange={e => updateFilter('max_rent', parseInt(e.target.value) || undefined)}
                  size="md"
                />
              </Box>
            </HStack>
          </VStack>
        </InfoCard>

        {/* Table Section */}
        <InfoCard
          title="Ansökningar"
          description="Klicka på en rad för att se fullständig information"
        >
          <Box p={0}>
            {delayedLoading ? (
              <Box p={8}>
                <VStack gap={3}>
                  <Skeleton height="40px" />
                  <Skeleton height="40px" />
                  <Skeleton height="40px" />
                  <Skeleton height="40px" />
                  <Skeleton height="40px" />
                </VStack>
              </Box>
            ) : applications && applications.length > 0 ? (
              <Box overflowX="auto">
                <Table.Root variant="outline" size="sm">
                  <Table.Header>
                    <Table.Row bg="bg.subtle">
                      <Table.ColumnHeader fontWeight="bold">Namn</Table.ColumnHeader>
                      <Table.ColumnHeader fontWeight="bold">Email</Table.ColumnHeader>
                      <Table.ColumnHeader fontWeight="bold">Telefon</Table.ColumnHeader>
                      <Table.ColumnHeader fontWeight="bold">Stad</Table.ColumnHeader>
                      <Table.ColumnHeader fontWeight="bold">Hyresintervall</Table.ColumnHeader>
                      <Table.ColumnHeader fontWeight="bold">Inkomst</Table.ColumnHeader>
                      <Table.ColumnHeader fontWeight="bold">Rökare</Table.ColumnHeader>
                      <Table.ColumnHeader fontWeight="bold">Arbetsgivare</Table.ColumnHeader>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {applications.map(app => (
                      <Table.Row
                        key={app.id}
                        cursor="pointer"
                        _hover={{
                          bg: 'blue.subtle',
                          transform: 'scale(1.001)',
                          transition: 'all 0.2s',
                        }}
                        onClick={() => navigate(`/admin/applications/${app.id}`)}
                      >
                        <Table.Cell fontWeight="medium">
                          {app.firstname} {app.lastname}
                        </Table.Cell>
                        <Table.Cell color="fg.muted">{app.email}</Table.Cell>
                        <Table.Cell color="fg.muted">{app.phone_number}</Table.Cell>
                        <Table.Cell>
                          <Badge colorPalette="blue" variant="subtle">
                            {app.city}
                          </Badge>
                        </Table.Cell>
                        <Table.Cell>
                          <Text fontSize="sm">
                            {app.rent.min.toLocaleString()} - {app.rent.max.toLocaleString()} kr
                          </Text>
                        </Table.Cell>
                        <Table.Cell>
                          <VStack align="start" gap={1}>
                            <Text fontSize="sm" fontWeight="medium">
                              {app.income.monthly.toLocaleString()} kr/mån
                            </Text>
                            <Badge size="xs" colorPalette="purple" variant="subtle">
                              {getIncomeTypeLabel(app.income.income_type)}
                            </Badge>
                          </VStack>
                        </Table.Cell>
                        <Table.Cell>
                          <Badge
                            colorPalette={app.smoker ? 'red' : 'green'}
                            variant="subtle"
                            size="sm"
                          >
                            {app.smoker ? 'Ja' : 'Nej'}
                          </Badge>
                        </Table.Cell>
                        <Table.Cell color="fg.muted" maxW="200px" truncate>
                          {app.employer || 'Ej angiven'}
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Root>
              </Box>
            ) : (
              <Box p={8} textAlign="center">
                <Text color="fg.muted" fontSize="lg">
                  Inga ansökningar hittades med de valda filtren
                </Text>
              </Box>
            )}
          </Box>
        </InfoCard>

        {/* Pagination */}
        <Pagination
          currentPage={pagination?.current_page || filters.page || 1}
          totalPages={pagination?.total_pages}
          totalItems={pagination?.total_count}
          itemsPerPage={pagination?.per_page || filters.limit || 10}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          isLoading={isLoading}
        />
      </VStack>
    </Box>
  );
};

export default AdminView;
